<template>
  <div class="p2p-page">
    <div class="p2p-between">
      <div class="p2p-stack" style="gap:6px">
        <h1 class="p2p-h1">Connections</h1>
        <p class="p2p-lead">The accounts your posts go to. Disconnect any of them at any time.</p>
      </div>
      <span v-if="demo" class="p2p-chip warn" title="Development mode: connections and publishing are simulated">Demo connections</span>
    </div>
    <div v-if="message" :class="['p2p-alert', messageTone]">{{ message }}</div>
    <div v-if="!available" class="p2p-alert">Account connections are being set up. Please check back soon.</div>

    <section class="p2p-stack">
      <h2 class="p2p-h2">Connected</h2>
      <div v-if="loading" class="p2p-skeleton" style="height:80px"></div>
      <div v-else-if="!accounts.length" class="p2p-empty">No accounts yet. Connect one below.</div>
      <div v-else class="p2p-stack" style="gap:10px">
        <div v-for="account in accounts" :key="account.id" class="p2p-delivery">
          <NetBadge :platform="account.platform" large />
          <div class="p2p-stack" style="gap:2px;flex:1 1 220px;min-width:0">
            <span class="p2p-h3">{{ account.display_name || account.username }}</span>
            <span class="p2p-faint">{{ account.label }}<span v-if="account.username"> · {{ account.username }}</span></span>
            <span v-if="account.status !== 'connected' && account.status_detail" class="p2p-faint">{{ account.status_detail }}</span>
            <span v-else-if="account.status === 'connected' && account.attention" class="p2p-faint">The last post to it was refused: {{ account.attention }}
              Check the account on the network, then <router-link to="/posts?tab=attention">retry it from Posts</router-link>.</span>
          </div>
          <span :class="['p2p-chip', health(account).tone]" :title="account.status_detail || ''"><span :class="['p2p-dot', health(account).tone]"></span>{{ health(account).label }}</span>
          <button v-if="account.status === 'connected'" type="button" class="p2p-btn sm ghost" :disabled="busy" @click="disconnect(account)">Disconnect</button>
          <button v-else type="button" class="p2p-btn sm soft" :disabled="busy || !available" @click="connect(account.platform)">Reconnect</button>
        </div>
      </div>
    </section>

    <section class="p2p-stack">
      <h2 class="p2p-h2">Add an account</h2>
      <p class="p2p-muted" style="margin:0">You approve each connection on the network's own sign-in page. {{ ownApps.length ? 'For the networks marked "our own app" that page names us; for the others it' : 'That page' }} may name <strong>Post for Me</strong>, the publishing service we use. We never see your social passwords, and your login tokens stay with Post for Me.</p>
      <div class="p2p-grid" style="grid-template-columns:repeat(auto-fill,minmax(min(220px,100%),1fr))">
        <button v-for="platform in platforms" :key="platform" type="button" class="p2p-tile" style="min-height:96px" :disabled="!available || busy" @click="connect(platform)">
          <NetBadge :platform="platform" large />
          <span><span class="p2p-h3">{{ label(platform) }}</span><br><span class="p2p-faint">{{ noteFor(platform) }}</span>
            <template v-if="ownApps.indexOf(platform) >= 0"><br><span class="p2p-faint">Our own app</span></template></span>
        </button>
      </div>
    </section>

    <v-dialog v-model="bluesky.open" max-width="480">
      <div class="p2p-card raised">
        <h2 class="p2p-h2">Connect Bluesky</h2>
        <p class="p2p-text">Bluesky uses an app password, never your account password. Create one in Bluesky under Settings, then App passwords.</p>
        <label class="p2p-label">Handle<input v-model="bluesky.handle" class="p2p-input" placeholder="you.bsky.social" autocomplete="off"></label>
        <label class="p2p-label">App password<input v-model="bluesky.password" class="p2p-input" type="password" autocomplete="off"></label>
        <div class="p2p-row">
          <button type="button" class="p2p-btn" :disabled="!bluesky.handle || !bluesky.password" @click="connect('bluesky', true)">Connect</button>
          <button type="button" class="p2p-btn ghost" @click="bluesky.open = false">Cancel</button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import NetBadge, { LABELS } from '../components/NetBadge.vue';
import { PLATFORM_ORDER } from '../components/steps/StepAccounts.vue';
import { post, message as text } from '../api';

export default {
  name: 'Connections',
  components: { NetBadge },
  data: () => ({ loading: true, busy: false, available: true, demo: false, message: '', messageTone: 'is-info', platforms: PLATFORM_ORDER,
    bluesky: { open: false, handle: '', password: '' },
    notes: { x: 'Text, images and video', linkedin: 'Company pages you manage', bluesky: 'Uses an app password', instagram: 'Business or creator accounts',
      facebook: 'Pages you manage', threads: 'Text, images and video', pinterest: 'Image and video pins', tiktok: 'Videos and photo posts', youtube: 'Shorts' } }),
  computed: {
    ownApps() { const config = this.$store.getters['publisher/config']; return (config && config.white_label) || []; },
    // One row per account: the connected one if there is one, otherwise its most recent disconnected row.
    accounts() {
      const all = this.$store.getters['publisher/accounts'];
      const key = a => a.platform + ':' + (a.username || '');
      const live = new Set(all.filter(a => a.status === 'connected').map(key));
      const latest = {};
      all.filter(a => a.status !== 'connected').forEach(a => { if (!latest[key(a)] || latest[key(a)].id < a.id) latest[key(a)] = a; });
      return all.filter(a => a.status === 'connected' || (!live.has(key(a)) && latest[key(a)] === a));
    }
  },
  async mounted() {
    if (this.$route.query.connected) {
      const count = Number(this.$route.query.connected) || 1;
      this.message = count === 1 ? 'Account connected. You can publish to it now.' : count + ' accounts connected. You can publish to them now.';
      this.messageTone = 'good';
    }
    this.$store.dispatch('publisher/loadConfig').catch(() => {});
    await this.load();
  },
  methods: {
    label(platform) { return LABELS[platform] || platform; },
    noteFor(platform) {
      // Post for Me's shared LinkedIn app connects company pages only; an approved app of our own may connect profiles.
      if (platform === 'linkedin' && this.ownApps.indexOf('linkedin') >= 0) return 'Your profile or company pages';
      return this.notes[platform];
    },
    // The four states people need to act on differently: fine, fix something, signed out by the network, or by you.
    health(account) {
      if (account.status === 'connected' && account.attention) return { label: 'Action required', tone: 'warn' };
      if (account.status === 'connected') return { label: 'Healthy', tone: 'good' };
      if (/network|expired|reconnect/i.test(account.status_detail || '')) return { label: 'Expired', tone: 'bad' };
      return { label: 'Disconnected', tone: '' };
    },
    async load() {
      try {
        const result = await this.$store.dispatch('publisher/loadAccounts');
        this.available = result.available;
        this.demo = result.demo;
      } catch (error) {
        this.message = text(error);
        this.messageTone = 'bad';
      } finally {
        this.loading = false;
      }
    },
    async connect(platform, confirmed) {
      if (platform === 'bluesky' && !confirmed) { this.bluesky.open = true; return; }
      this.busy = true;
      this.message = '';
      try {
        const payload = { platform };
        if (platform === 'bluesky') { payload.handle = this.bluesky.handle; payload.app_password = this.bluesky.password; }
        const result = await post('/social/connect', payload);
        sessionStorage.setItem('p2p-connect-return', '/connections');
        window.location.href = result.url;
      } catch (error) {
        this.message = text(error);
        this.messageTone = 'bad';
        this.busy = false;
      }
    },
    async disconnect(account) {
      this.busy = true;
      try {
        const result = await post('/social/accounts/' + account.id + '/disconnect');
        this.message = account.label + ' disconnected.' + (result.cancelled_deliveries === 1 ? ' Its scheduled post was cancelled.'
          : result.cancelled_deliveries ? ' Its ' + result.cancelled_deliveries + ' scheduled posts were cancelled.' : '');
        this.messageTone = 'is-info';
        await this.load();
      } catch (error) {
        this.message = text(error);
        this.messageTone = 'bad';
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>
